import { useEffect } from 'react'
import { db } from '@/lib/db'

const NOTIFIED_KEY = 'whistle-notified-ids'
const LOOKAHEAD_MS = 12 * 60 * 60 * 1000  // 12 hours

function getNotifiedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(NOTIFIED_KEY)
    return new Set(raw ? JSON.parse(raw) : [])
  } catch {
    return new Set()
  }
}

function markNotified(id: string) {
  const ids = getNotifiedIds()
  ids.add(id)
  // Prune to last 200 to prevent unbounded growth
  const trimmed = [...ids].slice(-200)
  localStorage.setItem(NOTIFIED_KEY, JSON.stringify(trimmed))
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

export function useEventReminders(seasonId: string | undefined, enabled: boolean) {
  useEffect(() => {
    if (!enabled || !seasonId) return
    if (!('Notification' in window) || Notification.permission !== 'granted') return

    let cancelled = false

    async function checkAndNotify() {
      if (cancelled) return
      const now = new Date()
      const cutoff = new Date(now.getTime() + LOOKAHEAD_MS).toISOString()
      const nowIso = now.toISOString()
      const notified = getNotifiedIds()

      // Check upcoming games
      const games = await db.games
        .where('seasonId').equals(seasonId!)
        .filter(g => g.gameDate > nowIso && g.gameDate < cutoff)
        .toArray()

      for (const game of games) {
        if (notified.has(game.id)) continue
        const diffMs = new Date(game.gameDate).getTime() - now.getTime()
        const diffH = Math.round(diffMs / 3_600_000)
        const timeStr = fmtTime(game.gameDate)
        const body = diffH <= 1
          ? `Game vs ${game.opponent} starts soon at ${timeStr}`
          : `Game vs ${game.opponent} today at ${timeStr}`
        new Notification('🏟️ Upcoming Game', { body, icon: '/pwa-192x192.png', tag: game.id })
        markNotified(game.id)
      }

      // Check upcoming events
      const events = await db.events
        .where('seasonId').equals(seasonId!)
        .filter(e => e.date > nowIso && e.date < cutoff)
        .toArray()

      const emojiMap: Record<string, string> = {
        practice: '🏃', meeting: '📋', scrimmage: '⚔️',
        tournament: '🏆', film: '🎬', deadline: '⏰', other: '📌',
      }

      for (const event of events) {
        if (notified.has(event.id)) continue
        const diffMs = new Date(event.date).getTime() - now.getTime()
        const diffH = Math.round(diffMs / 3_600_000)
        const timeStr = fmtTime(event.date)
        const emoji = emojiMap[event.type] ?? '📌'
        const body = diffH <= 1
          ? `${event.title} starts soon at ${timeStr}`
          : `${event.title} today at ${timeStr}`
        new Notification(`${emoji} ${event.type.charAt(0).toUpperCase() + event.type.slice(1)}`, {
          body, icon: '/pwa-192x192.png', tag: event.id,
        })
        markNotified(event.id)
      }
    }

    checkAndNotify()
    // Re-check every 30 minutes while app is open
    const interval = setInterval(checkAndNotify, 30 * 60 * 1000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [seasonId, enabled])
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied'
  if (Notification.permission === 'granted') return 'granted'
  return Notification.requestPermission()
}
